import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import AkfaPositionPicker from "./AkfaPositionPicker";
import AkfaCreateProductForm, { EMPTY_PRODUCT_FORM } from "./AkfaCreateProductForm";
import { getList } from "../../services/service";
import { createProductFromAkfa, getAkfaProductDraft, mapAkfaProduct } from "../../services/akfa/akfaService";

const BASE_URL = process.env.REACT_APP_HOST_BASE_URL;

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

// AKFA tovarini WinPro'ga bog'lash: 1) mavjud pozitsiyalarga (map) yoki 2) yangi mahsulot yaratib (create-product)
// initialMode="create" + lockMode — Mapping tabidagi "Yangi mahsulot sifatida yaratish" uchun (rejim almashtirgichsiz)
export default function ModalMoslash({ open, setOpen, akfaProduct, onSaved, initialMode = "map", lockMode = false }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const [mode, setMode] = useState("map");
    const [targets, setTargets] = useState([]);
    const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);
    const [textureIds, setTextureIds] = useState([]);
    const [draftLoaded, setDraftLoaded] = useState(false);
    const [lists, setLists] = useState({ series: [], units: [], suppliers: [], textures: [] });
    const [createListsLoaded, setCreateListsLoaded] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [savedMappings, setSavedMappings] = useState(null);

    useEffect(() => {
        if (!open) return;
        setMode(initialMode);
        setTargets([]);
        setProductForm(EMPTY_PRODUCT_FORM);
        setTextureIds([]);
        setDraftLoaded(false);
        setSubmitting(false);
        setSavedMappings(null);
        if (lists.series.length === 0) {
            getList(`${BASE_URL}/v1/product/series/list`, token).then((res) => {
                if (res.success) setLists((prev) => ({ ...prev, series: res.data || [] }));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, akfaProduct?.id]);

    useEffect(() => {
        if (!open || mode !== "create") return;
        if (!createListsLoaded) {
            (async () => {
                const [units, suppliers, textures] = await Promise.all([
                    getList(`${BASE_URL}/v1/unit/list`, token),
                    getList(`${BASE_URL}/v1/supplier/list`, token),
                    getList(`${BASE_URL}/v1/texture/list`, token),
                ]);
                setLists((prev) => ({
                    ...prev,
                    units: units.success ? units.data || [] : [],
                    suppliers: suppliers.success ? suppliers.data || [] : [],
                    textures: textures.success ? textures.data || [] : [],
                }));
                setCreateListsLoaded(true);
            })();
        }
        if (!draftLoaded) {
            setDraftLoaded(true);
            getAkfaProductDraft(token, akfaProduct.id).then((res) => {
                if (!res.success || !res.data) return;
                setProductForm((prev) => ({
                    ...prev,
                    ...Object.fromEntries(Object.entries(res.data).filter(([, v]) => v !== null && v !== undefined)),
                }));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, draftLoaded, akfaProduct?.id]);

    const close = () => setOpen(false);

    const finish = () => {
        close();
        onSaved?.();
    };

    const submitMap = async () => {
        if (targets.length === 0) {
            toast.warn(t("akfaIntegrationPage.modal.no_targets_selected"), { position: "bottom-right" });
            return;
        }
        setSubmitting(true);
        const response = await mapAkfaProduct(token, akfaProduct.id, {
            targets: targets.map((tgt) => ({ productId: tgt.productId, textureId: tgt.textureId })),
        });
        setSubmitting(false);
        if (!response.success) {
            toast.error(response.message || t("akfaIntegrationPage.modal.error_generic"), { position: "bottom-right" });
            return;
        }
        toast.success(t("akfaIntegrationPage.modal.map_success"), { position: "bottom-right", autoClose: 3000 });
        // Saqlangandan keyingi narx holati javobdan ko'rsatiladi
        setSavedMappings(response.data || []);
    };

    const submitCreate = async () => {
        if (!productForm.name?.trim() || !productForm.article?.trim() || !productForm.articleGroupType) {
            toast.warn(t("akfaIntegrationPage.create.required_fields"), { position: "bottom-right" });
            return;
        }
        if (textureIds.length === 0) {
            toast.warn(t("akfaIntegrationPage.create.no_textures_selected"), { position: "bottom-right" });
            return;
        }
        setSubmitting(true);
        const response = await createProductFromAkfa(token, akfaProduct.id, {
            product: {
                ...productForm,
                name: productForm.name.trim(),
                article: productForm.article.trim(),
                length: productForm.length === "" ? null : Number(productForm.length),
            },
            textureIds,
        });
        setSubmitting(false);
        if (!response.success) {
            toast.error(response.message || t("akfaIntegrationPage.modal.error_generic"), { position: "bottom-right" });
            return;
        }
        toast.success(t("akfaIntegrationPage.create.success", { name: response.data?.name || productForm.name }), {
            position: "bottom-right",
            autoClose: 3000,
        });
        finish();
    };

    const handleSubmit = (evt) => {
        evt.preventDefault();
        mode === "map" ? submitMap() : submitCreate();
    };

    if (!akfaProduct) return null;

    const infoItems = [
        [t("akfaIntegrationPage.modal.akfa_name"), akfaProduct.name],
        [t("akfaIntegrationPage.modal.akfa_code"), `#${akfaProduct.externalId}`],
        [t("akfaIntegrationPage.modal.akfa_group"), akfaProduct.groupName || "—"],
        [t("akfaIntegrationPage.modal.akfa_unit"), akfaProduct.unitName || "—"],
        [
            t("akfaIntegrationPage.modal.akfa_price"),
            akfaProduct.currentRate != null ? `${akfaProduct.currentRate} ${akfaProduct.currentRateCurrency || ""}` : "—",
        ],
        [t("akfaIntegrationPage.modal.akfa_date"), akfaProduct.currentRateDate || "—"],
    ];

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={savedMappings ? finish : setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative w-full text-left transition-all transform bg-white rounded-xl shadow-2xl sm:my-8 sm:w-full sm:max-w-3xl overflow-hidden">
                                <form onSubmit={handleSubmit} className="flex flex-col max-h-[85vh]">
                                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                                        <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                                            {lockMode
                                                ? t(`akfaIntegrationPage.modal.mode_${initialMode}`)
                                                : t("akfaIntegrationPage.modal.title")}
                                        </Dialog.Title>
                                        <button
                                            type="button"
                                            onClick={savedMappings ? finish : close}
                                            className="text-gray-400 hover:text-gray-500"
                                        >
                                            <XMarkIcon className="w-6 h-6" />
                                        </button>
                                    </div>

                                    <div className="flex-1 px-6 py-5 overflow-y-auto space-y-5">
                                        <div className="bg-gray-50 rounded-md p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                                            {infoItems.map(([label, value]) => (
                                                <div key={label}>
                                                    <div className="text-gray-500">{label}</div>
                                                    <div className="font-medium text-gray-900">{value}</div>
                                                </div>
                                            ))}
                                        </div>

                                        {savedMappings ? (
                                            <SavedMappingsPreview mappings={savedMappings} />
                                        ) : (
                                            <>
                                                <div className={classNames("flex gap-2", lockMode && "hidden")}>
                                                    {["map", "create"].map((m) => (
                                                        <button
                                                            key={m}
                                                            type="button"
                                                            onClick={() => setMode(m)}
                                                            className={classNames(
                                                                "px-3 py-1.5 text-sm font-medium rounded-md border",
                                                                mode === m
                                                                    ? "bg-indigo-600 text-white border-indigo-600"
                                                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                                            )}
                                                        >
                                                            {t(`akfaIntegrationPage.modal.mode_${m}`)}
                                                        </button>
                                                    ))}
                                                </div>

                                                {mode === "map" ? (
                                                    <AkfaPositionPicker
                                                        akfaProductId={akfaProduct.id}
                                                        series={lists.series}
                                                        targets={targets}
                                                        onChange={setTargets}
                                                    />
                                                ) : (
                                                    <AkfaCreateProductForm
                                                        form={productForm}
                                                        setForm={setProductForm}
                                                        textureIds={textureIds}
                                                        setTextureIds={setTextureIds}
                                                        lists={lists}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        {savedMappings ? (
                                            <button
                                                type="button"
                                                onClick={finish}
                                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                                            >
                                                {t("akfaIntegrationPage.common.close")}
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={close}
                                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                                >
                                                    {t("akfaIntegrationPage.modal.cancel")}
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={submitting}
                                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
                                                >
                                                    {submitting
                                                        ? t("akfaIntegrationPage.modal.saving")
                                                        : mode === "map"
                                                        ? t("akfaIntegrationPage.modal.save", { count: targets.length })
                                                        : t("akfaIntegrationPage.create.submit")}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

function SavedMappingsPreview({ mappings }) {
    const { t } = useTranslation();
    return (
        <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">{t("akfaIntegrationPage.modal.saved_title")}</h4>
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                {mappings.map((m) => (
                    <li key={m.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span>
                            {m.productName} · {m.textureName}
                        </span>
                        <span className="text-gray-700">
                            {m.pendingPrice != null ? (
                                <>
                                    {m.pendingBasePrice ?? "—"} {m.pendingBaseCurrencySymbol || ""} →{" "}
                                    <span className="font-semibold text-indigo-700">
                                        {m.pendingPrice} {m.pendingCurrencySymbol || ""}
                                    </span>
                                </>
                            ) : (
                                t(`akfaIntegrationPage.priceStatus.${m.priceStatus}`, { defaultValue: m.priceStatus })
                            )}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
